import { API, graphqlOperation } from "aws-amplify";
import { Option } from "components/parts/Input/SelectBox/SelectBox";
import { createUserLf, deleteUserLf, updateUserLf } from "graphql/mutations";
import {
  getUser,
  getAdminUserLf,
  isEmailExistLf,
  listUserByGroup,
  listUsers,
  listAdminGroupUserLf,
} from "graphql/queries";
import { getGroupQuery, listGroupsQuery } from "utils/apis/group";
import { listSelectableGroup, ListSelectableGroupResult } from "utils/queries";

import {
  ConsoleRoleType,
  GetUserQuery,
  Group,
  ListUserByGroupQuery,
  ListUsersQuery,
  Response,
  User,
  UserLfInput,
} from "../../../types/graphqlapi";

export const listUsersQuery = async (): Promise<User[] | undefined> => {
  try {
    const result = (await API.graphql(
      graphqlOperation(listUsers, {
        limit: 10000,
      })
    )) as { data: ListUsersQuery };
    const list = result.data.listUsers?.items;
    return list as User[];
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};

export const getUserQuery = async (
  email: string,
  group_id: string
): Promise<User | undefined> => {
  try {
    const result = (await API.graphql(
      graphqlOperation(listUsers, {
        limit: 10000,
        filter: {
          group_id: { eq: group_id },
          email: { eq: email },
        },
      })
    )) as { data: ListUsersQuery };
    const list = result.data.listUsers?.items;
    return list ? (list[0] ? list[0] : undefined) : undefined;
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};

export const isEmailExistQuery = async (email: string): Promise<boolean> => {
  const result = (await API.graphql(
    graphqlOperation(isEmailExistLf, {
      email,
    })
  )) as {
    data: { isEmailExistLf: Response };
  };

  const response = result.data.isEmailExistLf;
  if (!response.body) throw new Error("isEmailExist error");
  if (response.statusCode === "200") {
    return false;
  } else {
    return true;
  }
};

export const listSelectableGroupQuery = async (
  email: string
): Promise<Group[] | undefined> => {
  try {
    const result = (await API.graphql(
      graphqlOperation(listSelectableGroup, {
        email,
      })
    )) as { data: ListSelectableGroupResult };
    const undeletedUsers = result.data.listUsers?.items;
    if (!undeletedUsers?.[0]) throw undeletedUsers;
    const undeletedUser = undeletedUsers[0];
    const groupId = undeletedUser.group_id;
    const group = await getGroupQuery(groupId);
    if (!group) throw group;
    return [group];
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};

export type UserWithGroup =
  | User & {
      Group: Group;
    };

export const listManageUsersQuery = async (): Promise<
  UserWithGroup[] | undefined
> => {
  try {
    const result = (await API.graphql(
      graphqlOperation(listUsers, { limit: 10000 })
    )) as {
      data: ListUsersQuery;
    };
    const userList = result.data.listUsers?.items;
    if (!userList || userList?.length === 0) return;

    const userWithGrouplist = await Promise.all(
      userList.map(async (user) => {
        if (!user?.group_id) return;
        const group = await getGroupQuery(user.group_id);
        const obj = { ...user, Group: group };
        return obj;
      })
    );

    const list = userWithGrouplist.filter(
      (user) => user !== undefined
    ) as UserWithGroup[];

    // createAtの昇順でソート
    list.sort((a, b) => {
      if (!a || !b) return 0;
      if (a.Group && b.Group) {
        if (a.Group.createdAt < b.Group.createdAt) return -1;
        if (a.Group.createdAt > b.Group.createdAt) return 1;
      }
      if (a.Group && !b.Group) return -1;
      if (!a.Group && b.Group) return 1;

      let arole = 1;
      let brole = 1;
      if (a.role == ConsoleRoleType.GROUPMANAGER) arole = 2;
      if (a.role == ConsoleRoleType.GROUPUSER) arole = 3;
      if (b.role == ConsoleRoleType.GROUPMANAGER) brole = 2;
      if (b.role == ConsoleRoleType.GROUPUSER) brole = 3;
      if (arole < brole) return -1;
      if (arole > brole) return 1;

      if (a.email < b.email) return -1;
      if (a.email > b.email) return 1;

      return 0;
    });

    return list;
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};

export const listGroupAffiliationUsersQuery = async (): Promise<
  UserWithGroup[] | undefined
> => {
  try {
    const result = (await API.graphql(
      graphqlOperation(listUsers, {
        filter: {
          or: [
            { role: { eq: ConsoleRoleType.GROUPMANAGER } },
            { role: { eq: ConsoleRoleType.GROUPUSER } },
          ],
        },
        limit: 10000,
      })
    )) as {
      data: ListUsersQuery;
    };
    const userList = result.data.listUsers?.items;
    if (!userList || userList?.length === 0) return;

    const userWithGrouplist = await Promise.all(
      userList.map(async (user) => {
        if (!user?.group_id) return;
        const group = await getGroupQuery(user.group_id);
        const obj = { ...user, Group: group };
        return obj;
      })
    );

    const list = userWithGrouplist.filter(
      (user) => user !== undefined
    ) as UserWithGroup[];

    // createAtの昇順でソート
    list.sort((a, b) => {
      if (!a || !b) return 0;
      if (a.Group && b.Group) {
        if (a.Group.createdAt < b.Group.createdAt) return -1;
        if (a.Group.createdAt > b.Group.createdAt) return 1;
      }
      if (a.Group && !b.Group) return -1;
      if (!a.Group && b.Group) return 1;

      let arole = 1;
      let brole = 1;
      if (a.role == ConsoleRoleType.GROUPMANAGER) arole = 2;
      if (a.role == ConsoleRoleType.GROUPUSER) arole = 3;
      if (b.role == ConsoleRoleType.GROUPMANAGER) brole = 2;
      if (b.role == ConsoleRoleType.GROUPUSER) brole = 3;
      if (arole < brole) return -1;
      if (arole > brole) return 1;

      if (a.email < b.email) return -1;
      if (a.email > b.email) return 1;

      return 0;
    });

    return list;
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};

export const listManageUsersByGroupQuery = async (
  group_id: string
): Promise<UserWithGroup[] | undefined> => {
  try {
    const result = (await API.graphql(
      graphqlOperation(listUserByGroup, {
        limit: 10000,
        group_id: group_id,
      })
    )) as {
      data: ListUserByGroupQuery;
    };

    const list = result?.data?.listUserByGroup?.items as UserWithGroup[];
    // createAtの昇順でソート
    list.sort((a, b) => {
      let arole = 1;
      let brole = 1;
      if (a.role == ConsoleRoleType.GROUPMANAGER) arole = 2;
      if (a.role == ConsoleRoleType.GROUPUSER) arole = 3;
      if (b.role == ConsoleRoleType.GROUPMANAGER) brole = 2;
      if (b.role == ConsoleRoleType.GROUPUSER) brole = 3;
      if (arole < brole) return -1;
      if (arole > brole) return 1;

      if (a.email < b.email) return -1;
      if (a.email > b.email) return 1;

      return 0;
    });

    return list;
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};

export const createUserMutation = async (
  input: UserLfInput,
  id: string
): Promise<string | undefined> => {
  try {
    const result = (await API.graphql(
      graphqlOperation(createUserLf, {
        input,
        id,
      })
    )) as {
      data: { createUserLf: Response };
    };
    if (!result.data.createUserLf.body) throw new Error("createUser error");
    const resultJson = JSON.parse(result.data.createUserLf.body);
    if (!resultJson.Result.id) throw resultJson;
    const userId = resultJson.Result.id;
    return userId;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const updateUserMutation = async (
  input: UserLfInput,
  id: string
): Promise<string | undefined> => {
  try {
    const result = (await API.graphql(
      graphqlOperation(updateUserLf, {
        input,
        id,
      })
    )) as {
      data: { updateUserLf: Response };
    };
    if (!result.data.updateUserLf.body) throw new Error("updateUser error");
    const resultJson = JSON.parse(result.data.updateUserLf.body);
    if (!resultJson.Result.id) throw resultJson;
    const userId = resultJson.Result.id;
    return userId;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const deleteUserMutation = async (
  input: UserLfInput,
  id: string
): Promise<string | undefined> => {
  try {
    const result = (await API.graphql(
      graphqlOperation(deleteUserLf, {
        input,
        id,
      })
    )) as {
      data: { deleteUserLf: Response };
    };
    if (!result.data.deleteUserLf.body) throw new Error("deleteUser error");
    const resultJson = JSON.parse(result.data.deleteUserLf.body);
    if (!resultJson.Result.id) throw resultJson;
    const userId = resultJson.Result.id;
    return userId;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getGroupSelectOptions = async (): Promise<Option[]> => {
  const tmp = await listGroupsQuery();
  const result = tmp?.map((x: Group) => ({
    value: x.id,
    text: x.name,
  }));
  return result ? result : [];
};

/**
 * 特定のロールをもつユーザーのリストを取得する
 * @returns {Id,name}がペアとなった配列
 */
export const listAdminGroupUserQuery = async (userId: string) => {
  try {
    const result = (await API.graphql(
      graphqlOperation(listAdminGroupUserLf, {
        id: userId,
      })
    )) as {
      data: { listAdminGroupUserLf: Response };
    };

    if (!result.data.listAdminGroupUserLf.body)
      throw new Error("listAdminGroupUserLf error");

    const resultJson = JSON.parse(result.data.listAdminGroupUserLf.body);
    const userList = resultJson.userList;

    if (!userList) {
      return [];
    }

    const returnUserList = userList.map(
      (item: { id: string; name: string }) => {
        return {
          value: item ? item.id : "",
          text: item ? item.name : "",
        };
      }
    );

    return returnUserList;
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};

/**
 * 特定のグループをもつユーザーのリストを取得する
 * @returns {Id,name}がペアとなった配列
 */
export const listUsersByGroupQuery = async (groupId: string) => {
  try {
    const result = (await API.graphql(
      graphqlOperation(listUserByGroup, {
        group_id: groupId,
      })
    )) as {
      data: ListUserByGroupQuery;
    };

    if (!result.data?.listUserByGroup?.items) {
      return [];
    }

    const userList = result.data.listUserByGroup.items.map((items) => {
      return {
        value: items ? items.id : "",
        text: items ? items.name : "",
      };
    });

    return userList;
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};

/**
 * 何かしらグループに所属しているユーザーのリストを取得する
 * @returns {id,name}がペアとなった配列
 */
export const listGroupAffiliationUsers = async () => {
  try {
    const result = (await API.graphql(
      graphqlOperation(listUsers, {
        filter: {
          or: [
            { role: { eq: ConsoleRoleType.GROUPMANAGER } },
            { role: { eq: ConsoleRoleType.GROUPUSER } },
          ],
        },
        limit: 10000,
      })
    )) as {
      data: ListUsersQuery;
    };

    if (!result.data?.listUsers?.items) {
      return [];
    }

    const userList = result.data.listUsers.items.map((items) => {
      return {
        value: items ? items.id : "",
        text: items ? items.name : "",
        optional: items ? items.group_id : "",
      };
    });

    return userList;
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};

export const getUserByIdQuery = async (
  id: string
): Promise<User | null | undefined> => {
  try {
    const result = (await API.graphql(
      graphqlOperation(getUser, {
        id: id,
      })
    )) as { data: GetUserQuery };
    return result.data.getUser;
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};

export const getAdminUserQuery = async (id: string) => {
  try {
    const result = (await API.graphql(
      graphqlOperation(getAdminUserLf, {
        user_id: id,
      })
    )) as { data: { getAdminUserLf: Response } };

    if (!result.data.getAdminUserLf.body)
      throw new Error("getAdminUserLf error");

    const resultJson = JSON.parse(result.data.getAdminUserLf.body);

    return resultJson.Result as User;
  } catch (e) {
    // TODO エラーハンドリング
    console.error(e);
    throw e;
  }
};
